import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import InputBase from '@mui/material/InputBase';
import Skeleton from '@mui/material/Skeleton';
import SwipeableDrawer from '@mui/material/SwipeableDrawer';
import Typography from '@mui/material/Typography';
import {
  getMediaUrl,
  getSession,
  useGetAllOnlineUsersQuery,
  useGetAllProfileQuery,
  useGetCommunityStats,
  useGetTopContributors,
  useGetTrendingTags,
} from '@onesource/services';
import { useMemo, useState, type FC, type ReactNode } from 'react';
import { HiOutlineChartBar, HiOutlineTag, HiOutlineTrophy, HiOutlineUserGroup, HiOutlineUsers } from 'react-icons/hi2';
import { MdOutlineArticle, MdSearch } from 'react-icons/md';
import { EFeatureFlagEnum, isFeatureFlagEnabled } from 'src/hooks/use-feature-flag-utils';

const REFRESH_INTERVAL_MS = 30 * 1000;
const FONT = 'Figtree, sans-serif';

/** Centered empty / placeholder text, shared by the panels. */
const EmptyNote: FC<{ children: ReactNode }> = ({ children }) => (
  <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#9a9a9a', textAlign: 'center', py: 4 }}>
    {children}
  </Typography>
);

/** A few list-row skeletons while a panel's data loads. */
const RowSkeletons: FC<{ rows?: number }> = ({ rows = 5 }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
    {Array.from({ length: rows }).map((_, index) => (
      // oxlint-disable-next-line react/no-array-index-key
      <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
        <Skeleton variant="circular" width={38} height={38} />
        <Box sx={{ flex: 1 }}>
          <Skeleton variant="text" width="45%" height={16} />
          <Skeleton variant="text" width="30%" height={12} />
        </Box>
      </Box>
    ))}
  </Box>
);

const StatsPanel: FC<{ spaceId: string }> = ({ spaceId }) => {
  const { data, isLoading } = useGetCommunityStats(spaceId);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', gap: 1.5 }}>
        <Skeleton variant="rounded" height={104} sx={{ flex: 1, borderRadius: '16px' }} />
        <Skeleton variant="rounded" height={104} sx={{ flex: 1, borderRadius: '16px' }} />
      </Box>
    );
  }

  if (!data) {
    return <EmptyNote>Stats aren’t available yet.</EmptyNote>;
  }

  const tiles = [
    { icon: <HiOutlineUsers size={15} />, value: data.totalMembers, label: 'Members' },
    { icon: <MdOutlineArticle size={15} />, value: data.postsThisMonth, label: 'Posts this month' },
  ];

  return (
    <Box sx={{ display: 'flex', gap: 1.5 }}>
      {tiles.map(tile => (
        <Box
          key={tile.label}
          sx={{
            flex: 1,
            minWidth: 0,
            bgcolor: '#f6f4f3',
            borderRadius: '16px',
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 1.25,
          }}
        >
          {/* Icon + label on one line, then the big number below */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
            <Box sx={{ display: 'flex', color: 'primary.main', flexShrink: 0 }}>{tile.icon}</Box>
            <Typography noWrap sx={{ fontFamily: FONT, fontSize: 12.5, fontWeight: 600, color: '#8a8a8a' }}>
              {tile.label}
            </Typography>
          </Box>
          <Typography sx={{ fontFamily: FONT, fontSize: 26, fontWeight: 800, color: '#1a1a2e', lineHeight: 1 }}>
            {(tile.value ?? 0).toLocaleString()}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

const OnlinePanel: FC = () => {
  const [search, setSearch] = useState('');
  const token = getSession();
  const { data: profile } = useGetAllProfileQuery({ token });
  const currentUserId = profile?.id?.toString();
  const { data, isLoading } = useGetAllOnlineUsersQuery({ enabled: !!token, refetchInterval: REFRESH_INTERVAL_MS });

  const users = useMemo(() => {
    const seen = new Set<string>();
    const shaped = (data?.users ?? [])
      .filter(user => !user.isPrivate && user.id !== currentUserId)
      .filter(user => {
        if (seen.has(user.id)) {
          return false;
        }
        seen.add(user.id);
        return true;
      })
      .sort((a, b) => (a.isActive === b.isActive ? b.timestamp - a.timestamp : a.isActive ? -1 : 1));

    const query = search.trim().toLowerCase();
    return query ? shaped.filter(user => user.name?.toLowerCase().includes(query)) : shaped;
  }, [data?.users, currentUserId, search]);

  if (isLoading) {
    return <RowSkeletons />;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          bgcolor: '#f4f2f1',
          borderRadius: '12px',
          px: 1.5,
          height: 42,
        }}
      >
        <MdSearch size={18} color="#9a9a9a" />
        <InputBase
          value={search}
          onChange={event => setSearch(event.target.value)}
          placeholder="Search people…"
          sx={{ flex: 1, fontFamily: FONT, fontSize: 14 }}
        />
      </Box>

      {users.length === 0 ? (
        <EmptyNote>{search ? 'No one matches that name.' : 'No one else is online right now.'}</EmptyNote>
      ) : (
        users.map(user => (
          <Box key={user.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <Box sx={{ position: 'relative' }}>
              <Avatar src={user.avatarUrl || undefined} alt={user.name} sx={{ width: 38, height: 38 }} />
              <Box
                sx={{
                  position: 'absolute',
                  right: -1,
                  bottom: -1,
                  width: 11,
                  height: 11,
                  borderRadius: '50%',
                  border: '2px solid #fff',
                  bgcolor: user.isActive ? '#37d187' : '#c9c9c9',
                }}
              />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography noWrap sx={{ fontFamily: FONT, fontSize: 14, fontWeight: 600, color: '#1a1a2e' }}>
                {user.name}
              </Typography>
              {user.username && (
                <Typography noWrap sx={{ fontFamily: FONT, fontSize: 12, color: '#9a9a9a' }}>
                  @{user.username}
                </Typography>
              )}
            </Box>
          </Box>
        ))
      )}
    </Box>
  );
};

interface TrendingPanelProps {
  spaceId: string;
  selectedTagValues?: string[];
  onTagClick: (value: string) => void;
}

const TrendingPanel: FC<TrendingPanelProps> = ({ spaceId, selectedTagValues, onTagClick }) => {
  const { data, isLoading } = useGetTrendingTags(spaceId);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {Array.from({ length: 8 }).map((_, index) => (
          // oxlint-disable-next-line react/no-array-index-key
          <Skeleton key={index} variant="rounded" width={84} height={30} sx={{ borderRadius: '15px' }} />
        ))}
      </Box>
    );
  }

  const tags = data?.tags ?? [];
  if (tags.length === 0) {
    return <EmptyNote>Nothing trending this week.</EmptyNote>;
  }

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
      {tags.map(tag => {
        const isSelected = selectedTagValues?.includes(tag.value) ?? false;
        return (
          <Chip
            key={tag.value}
            label={`#${tag.label} · ${tag.count}`}
            onClick={() => onTagClick(tag.value)}
            sx={{
              height: 34,
              borderRadius: '17px',
              px: 0.5,
              bgcolor: theme => (isSelected ? theme.palette.primary.main : '#f4f2f1'),
              color: theme => (isSelected ? theme.palette.common.white : '#3a3a4a'),
              fontFamily: FONT,
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
              '&:hover': { bgcolor: theme => (isSelected ? theme.palette.primary.dark : '#ece9e8') },
              '& .MuiChip-label': { px: '12px' },
            }}
          />
        );
      })}
    </Box>
  );
};

const RANK_COLORS = ['#e8b53d', '#b8b8c0', '#cd8b5b'];

const ContributorsPanel: FC<{ spaceId: string }> = ({ spaceId }) => {
  const { data, isLoading } = useGetTopContributors(spaceId);

  if (isLoading) {
    return <RowSkeletons />;
  }

  const contributors = data?.contributors ?? [];
  if (contributors.length === 0) {
    return <EmptyNote>No contributors yet this month.</EmptyNote>;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {contributors.map((contributor, index) => (
        <Box key={contributor.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
          <Box
            sx={{
              width: 22,
              height: 22,
              flexShrink: 0,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontFamily: FONT,
              fontSize: 12,
              fontWeight: 800,
              color: index < 3 ? '#fff' : '#9a9a9a',
              bgcolor: index < 3 ? RANK_COLORS[index] : '#f0eeed',
            }}
          >
            {index + 1}
          </Box>
          <Avatar src={getMediaUrl(contributor.avatar)} alt={contributor.name} sx={{ width: 38, height: 38 }} />
          <Box sx={{ minWidth: 0 }}>
            <Typography noWrap sx={{ fontFamily: FONT, fontSize: 14, fontWeight: 600, color: '#1a1a2e' }}>
              {contributor.name}
            </Typography>
            <Typography sx={{ fontFamily: FONT, fontSize: 12, color: '#9a9a9a' }}>
              {contributor.replyCount} {contributor.replyCount === 1 ? 'reply' : 'replies'} this month
            </Typography>
          </Box>
        </Box>
      ))}
    </Box>
  );
};

interface CommunityDetailsDrawerProps {
  open: boolean;
  onClose: () => void;
  spaceId: string;
  /** Currently applied feed tag-filter values, to highlight active tags. */
  selectedTagValues?: string[];
  /** Toggles the tag in the feed's tag filter. */
  onTagClick: (value: string) => void;
}

/**
 * Mobile detail sheet behind the banner stats bar's "Community" button. The bar
 * shows the at-a-glance numbers; this drag-to-dismiss bottom sheet holds the
 * full community info in mobile-native panels (not the desktop sidebar cards),
 * split across segmented pill tabs so each gets room to breathe: who's online
 * (searchable), trending tags, top contributors, community stats.
 */
export const CommunityDetailsDrawer: FC<CommunityDetailsDrawerProps> = ({
  open,
  onClose,
  spaceId,
  selectedTagValues,
  onTagClick,
}) => {
  const [tab, setTab] = useState(0);

  // The Trending / Contributors / Stats panels mirror the desktop sidebar cards,
  // so they hide behind the same feature flag; Online is always available.
  const showCommunityWidgets = isFeatureFlagEnabled(EFeatureFlagEnum.SHOW_COMMUNITY_WIDGETS);

  const tabs = useMemo(() => {
    const items: Array<{ key: string; label: string; icon: ReactNode; render: () => ReactNode }> = [
      { key: 'online', label: 'Online', icon: <HiOutlineUserGroup size={16} />, render: () => <OnlinePanel /> },
    ];
    if (showCommunityWidgets) {
      items.push(
        {
          key: 'trending',
          label: 'Trending',
          icon: <HiOutlineTag size={16} />,
          render: () => (
            <TrendingPanel spaceId={spaceId} selectedTagValues={selectedTagValues} onTagClick={onTagClick} />
          ),
        },
        {
          key: 'contributors',
          label: 'Contributors',
          icon: <HiOutlineTrophy size={16} />,
          render: () => <ContributorsPanel spaceId={spaceId} />,
        },
        {
          key: 'stats',
          label: 'Stats',
          icon: <HiOutlineChartBar size={16} />,
          render: () => <StatsPanel spaceId={spaceId} />,
        },
      );
    }
    return items;
  }, [showCommunityWidgets, spaceId, selectedTagValues, onTagClick]);

  // Clamp in case the flag flips off while a hidden tab was selected.
  const activeTab = Math.min(tab, tabs.length - 1);

  return (
    <SwipeableDrawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      onOpen={() => undefined}
      disableSwipeToOpen
      PaperProps={{
        sx: {
          borderRadius: '22px 22px 0 0',
          // Open to a comfortable minimum even when a tab has little content,
          // and never past most of the screen.
          minHeight: '45vh',
          maxHeight: '90dvh',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: '#fff',
        },
      }}
    >
      {/* Grab handle — drag the sheet down to dismiss */}
      <Box sx={{ display: 'flex', justifyContent: 'center', pt: '10px', pb: '4px', flexShrink: 0 }}>
        <Box sx={{ width: 42, height: 5, borderRadius: 3, bgcolor: '#e2dddb' }} />
      </Box>

      {/* Simple header */}
      <Typography
        sx={{ fontFamily: FONT, fontSize: 15, fontWeight: 700, color: '#1a1a2e', textAlign: 'center', pt: 0.5, pb: 1 }}
      >
        Community
      </Typography>

      {/* Segmented tabs — a single track split into equal-width segments (icon
          over label) so they always fit the screen without horizontal scroll.
          Hidden when only Online remains (nothing to switch between). */}
      {tabs.length > 1 && (
        <Box
          sx={{
            display: 'flex',
            gap: 0.5,
            mx: 2,
            mb: 1.5,
            p: '4px',
            flexShrink: 0,
            borderRadius: '14px',
            bgcolor: '#f4f2f1',
          }}
        >
          {tabs.map((item, index) => {
            const active = activeTab === index;
            return (
              <Box
                key={item.label}
                component="button"
                type="button"
                onClick={() => setTab(index)}
                sx={{
                  flex: 1,
                  minWidth: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 0.4,
                  border: 'none',
                  cursor: 'pointer',
                  py: 0.85,
                  px: 0.5,
                  borderRadius: '11px',
                  whiteSpace: 'nowrap',
                  fontFamily: FONT,
                  fontSize: 11.5,
                  fontWeight: 600,
                  transition: 'background-color 0.15s ease, color 0.15s ease, box-shadow 0.15s ease',
                  bgcolor: active ? '#fff' : 'transparent',
                  color: active ? 'primary.main' : '#9a9a9a',
                  boxShadow: active ? '0 1px 3px rgba(0,0,0,0.12)' : 'none',
                }}
              >
                <Box sx={{ display: 'flex', color: active ? 'primary.main' : '#9a9a9a' }}>{item.icon}</Box>
                {item.label}
              </Box>
            );
          })}
        </Box>
      )}

      {/* Scrollable tab content — minHeight:0 lets this flex child actually
          scroll (e.g. a long online list) instead of overflowing the sheet. */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          overscrollBehavior: 'contain',
          px: 2,
          pt: 0.5,
          pb: 'calc(16px + env(safe-area-inset-bottom))',
        }}
      >
        {tabs[activeTab]?.render()}
      </Box>
    </SwipeableDrawer>
  );
};
